import { ElementType, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import cls from 'classnames';

import { useStore } from '@/stores/StoreProvider';
import { svgStringToHTML } from '@/utils/svgStringToHTML';

import { INCLUSION_TYPES } from './Clarity.constants';
import styles from './Clarity.module.scss';

export interface InclusionsType {
  code: string;
  img: ElementType;
  title: string;
}

let animationTimeout: NodeJS.Timeout;

function Clarity() {
  const { diamond } = useStore('diamondsStore');

  const [svgContent, setSvgContent] = useState<Nullable<string>>(null);
  const [inclusions, setInclusions] = useState<InclusionsType[]>([]);

  const [activeInclusion, setActiveInclusion] = useState('All');
  const [isAnimated, setAnimated] = useState(false);

  function animateInclusion() {
    clearTimeout(animationTimeout);
    animationTimeout = setTimeout(() => {
      setAnimated(false);
    }, 5000);
  }

  function onSelectInclusion(inclusionType: string) {
    setActiveInclusion(inclusionType);

    setAnimated(inclusionType !== 'All');
    animateInclusion();
  }

  async function getSVGContent(path: string) {
    try {
      const svgElement = await svgStringToHTML(path);
      const resultedInclusionTypes: InclusionsType[] = [];

      INCLUSION_TYPES.forEach((inclusionType) => {
        const isInclusionTypeExist = Boolean(svgElement?.querySelector(`[inclusion_type='${inclusionType.code}']`));

        if (isInclusionTypeExist) {
          resultedInclusionTypes.push(inclusionType);
        }
      });

      setInclusions(resultedInclusionTypes);
      setSvgContent(svgElement?.outerHTML ?? null);
    } catch (e) {
      //
    }
  }

  useEffect(() => {
    if (diamond?.plottingDiagram) {
      getSVGContent(diamond.plottingDiagram);
    }
  }, [diamond?.plottingDiagram]);

  if (!svgContent) {
    return null;
  }

  return (
    <div className={styles.root}>
      <div className="container">
        <h2 className={styles.title}>Clarity Diagram</h2>
      </div>

      <div className="container">
        <div className={cls([styles.diagram, styles[`clarityInclusion${activeInclusion.replaceAll(' ', '')}`], { [styles.diagramAnimated]: isAnimated }])}>
          <div className={styles.plotting} dangerouslySetInnerHTML={{ __html: svgContent }} />
        </div>
      </div>

      <div className="container">
        {inclusions.length > 0 && (
          <div className={styles.inclusions}>
            <div className={styles.inclusionsContent}>
              <div className={cls(styles.inclusion, { [styles.inclusionActive]: activeInclusion === 'All' })}>
                <div className={styles.inclusionWrapper}>
                  <div className={styles.inclusionContent} onClick={() => onSelectInclusion('All')}>
                    <div className={styles.inclusionImage} />

                    <div className={styles.inclusionTitle}>All</div>
                  </div>
                </div>
              </div>

              {inclusions.map((inclusion) => {
                const Icon = inclusion.img;

                return (
                  <div
                    key={inclusion.code}
                    className={cls(styles.inclusion, {
                      [styles.inclusionActive]: activeInclusion === inclusion.code,
                    })}
                  >
                    <div className={styles.inclusionWrapper}>
                      <div className={styles.inclusionContent} onClick={() => onSelectInclusion(inclusion.code)}>
                        <div className={styles.inclusionImage}>
                          <Icon width="33" height="33" />
                        </div>

                        <div className={styles.inclusionTitle}>{inclusion.title}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default observer(Clarity);
